import { makeAutoObservable } from "mobx";

import { getGanttModelsByProjectId, updateGanttModel } from "@synthesis/shared-services/project-gge/projectGanttController/client";
import { setSnack } from "@synthesis/shared-ui";

const normalizeBaseName = (s: string) => String(s || "").replace(/\.ifc$/i, "");

class WorkerStore {
  worker: Worker | null = null;
  progress: { [fileId: string]: number } = {};
  totalProgress = 0;
  uploading = false;
  filesCount = 0;
  completedFiles = 0;
  status: string | null = null;
  uploadedFileIds: { [oldFileId: string]: string } = {};
  pendingByProject: Record<
    string,
    Record<
      string,
      {
        rawName: string;
        baseName: string;
        size: number;
        percent: number;
        status: "progress" | "success" | "error";
        updatedAt: number;
      }
    >
  > = {};

  constructor() {
    makeAutoObservable(this);
    this.worker = new Worker("/file-upload.js", { type: "module" });
    if (this.worker) {
      this.worker.addEventListener("message", this.handleWorkerMessage);
    }
  }

  handleWorkerMessage = (event: MessageEvent) => {
    const { type, fileName, error, response, currentProjectId, fileSize, percent } = event.data;
    const projectId = String(currentProjectId || "");
    const baseName = normalizeBaseName(String(fileName || ""));

    if (projectId) {
      if (!this.pendingByProject[projectId]) this.pendingByProject[projectId] = {};
      const prev = this.pendingByProject[projectId][baseName];
      this.pendingByProject[projectId][baseName] = {
        rawName: String(fileName || prev?.rawName || ""),
        baseName,
        size: Number(fileSize ?? prev?.size ?? 0),
        percent: Number(percent ?? prev?.percent ?? 0),
        status: type === "success" ? "success" : type === "error" ? "error" : "progress",
        updatedAt: Date.now(),
      };
    }
    if (type === "progress") {
      this.updateProgress(fileName, event.data.percent);
    } else if (type === "success") {
      this.updateProgress(fileName, 100);
      setSnack({
        messageType: "success",
        title: `${fileName} успешно загружен`,
      });
    } else if (type === "error") {
      this.updateProgress(fileName, 100);
      let message;
      try {
        const jsonParse = response ? JSON.parse(response) : null;
        if (jsonParse && jsonParse?.debugMessage?.includes("File with name")) {
          message = `Файл ${fileName} уже существует`;
        } else {
          message = error;
        }
      } catch {
        message = error;
      }
      setSnack({
        messageType: "error",
        title: message,
      });
    }
  };

  getPendingForProject(projectId: string) {
    return this.pendingByProject[projectId] || {};
  }

  clearPendingForProjectBaseName(projectId: string, baseName: string) {
    if (!this.pendingByProject[projectId]) return;
    delete this.pendingByProject[projectId][baseName];
  }

  addMessageListener(listener: (event: MessageEvent) => void) {
    if (this.worker) {
      this.worker.addEventListener("message", listener);
    }
  }

  removeMessageListener(listener: (event: MessageEvent) => void) {
    if (this.worker) {
      this.worker.removeEventListener("message", listener);
    }
  }

  postMessage(message: unknown) {
    if (this.worker) {
      // eslint-disable-next-line unicorn/require-post-message-target-origin
      this.worker.postMessage(message);
    }
  }

  setStatus(status: string | null) {
    this.status = status;
  }

  updateProgress(fileId: string, percent: number) {
    this.progress[fileId] = percent;
    const total = this.filesCount > 0 ? Object.values(this.progress).reduce((acc, p) => acc + p, 0) / this.filesCount : 0;
    this.totalProgress = total;
    if (total === 100) {
      this.setUploading(false);
    }
  }

  setFilesCount(count: number) {
    this.filesCount = count;
  }

  setCompletedFiles(count: number) {
    this.completedFiles = count;
  }

  setUploading(isUploading: boolean) {
    this.uploading = isUploading;
  }

  clearProgress() {
    this.progress = {};
    this.totalProgress = 0;
  }

  clearFiles() {
    this.clearProgress();
    this.filesCount = 0;
    this.completedFiles = 0;
    this.uploading = false;
  }

  setUploadedFileIds({ oldFileId, newFileId }: { oldFileId: string; newFileId: string }) {
    this.uploadedFileIds[oldFileId] = newFileId;
  }
  updateGanttModel({ modelId, projectId, newModelId, typeEvent }: { modelId: string; projectId: string; newModelId: string; typeEvent: string }) {
    if (typeEvent === "updateVersion") {
      void (async () => {
        const models = (await getGanttModelsByProjectId({ path: { projectId } })).data;

        if (models) {
          const ganttModels = models.map((model) => model.modelId);

          const newGanttModels = [...ganttModels];
          newGanttModels[ganttModels.indexOf(modelId)] = newModelId;
          await updateGanttModel({ path: { modelId }, query: { projectId, newModelId } });
        }
      })();
    }
  }
}

export const workerStore = new WorkerStore();
